/**
 * School modal · Outcomes block for Product Insights · LAE (Schools mode).
 *
 * Ports LAE `features/productivity/SchoolOutcomesBlock.jsx` and `shared/OutcomesSummaryTable.jsx`:
 * the Schools table's outcome column groups for one school, first as a four-row summary table
 * (consolidated over the school's groups), then as the Schools table itself on the outcome groups
 * ("Outcomes by assignment"), split per assignment when the school has more than one line.
 *
 * Deliberate differences from LAE:
 * - No drill-downs (owner 2026-10-29): `onDrill` and `ClickableCount` are gone; counts are plain text.
 * - No definition tooltips (spec §0.4): the "Share of Total Leads." hover on "% of leads" and the
 *   outcome-definition hovers on each row label are dropped.
 * - The consolidation rule lives in `./lae-school-rows` (`consolidateOutcomes`, P6), not here.
 * - Avg call time uses `fmtShortDuration` (spec §7.2 recommendation): LAE's `fmtDuration` showed `0m`
 *   for averages under a minute. A missing talk-time figure gives `—`, never an average over 0 seconds.
 * - Bar fills are tokens: chart-1 / success / danger / warning for LAE's brand / emerald / red / amber.
 * - Below `md` the summary table becomes a stacked card list with the same columns.
 * - `shared/outcomeTiles.js` is not ported: only the counsellor modal (out of scope) used it.
 */
import { useMemo, type ReactNode } from 'react';
import { Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import { cx } from '@/lib/cx';
import type { LaeProdGroupRow, LaeProdMetrics } from '@/lib/types';
import { PiCardField, PiCardList } from '../pi-kit';
import { fmtDuration, fmtNum, fmtShortDuration } from './lae-format';
import { SCHOOL_OUTCOME_GROUPS } from './lae-school-columns';
import { consolidateOutcomes } from './lae-school-rows';
import { SchoolsTable } from './SchoolsTable';

const DASH = '—';

/** One summary row per outcome: the students metric, its answered-calls and talk-seconds keys, the bar fill. */
interface OutcomeRowDef {
  metric: 'counselling_completed' | 'advanced' | 'regressed' | 'applications';
  label: string;
  calls?: 'ccm_calls' | 'adv_calls' | 'neg_calls';
  secs?: 'ccm_talk_seconds' | 'adv_talk_seconds' | 'neg_talk_seconds';
  bar: string;
}

const ROWS: readonly OutcomeRowDef[] = [
  { metric: 'counselling_completed', label: 'Interested in 2027 Intakes', calls: 'ccm_calls', secs: 'ccm_talk_seconds', bar: 'bg-chart-1' },
  { metric: 'advanced', label: 'Positive status changes', calls: 'adv_calls', secs: 'adv_talk_seconds', bar: 'bg-success' },
  { metric: 'regressed', label: 'Negative status changes', calls: 'neg_calls', secs: 'neg_talk_seconds', bar: 'bg-danger' },
  { metric: 'applications', label: 'Applications', bar: 'bg-warning' },
];

const asRecord = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' ? (v as Record<string, unknown>) : {};

/** A finite number, or null for null / undefined / '' / junk. Never 0 for a missing figure. */
function num(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v !== 'number' && typeof v !== 'string') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Share of the school's leads in percent: 2 dp under 1%, else 1 dp. null when either side is missing or leads is 0. */
function shareOfLeads(n: number | null, leads: number | null): number | null {
  if (n === null || leads === null || leads === 0) return null;
  const p = (n / leads) * 100;
  if (!p) return 0;
  return p < 1 ? Number(p.toFixed(2)) : Number(p.toFixed(1));
}

interface OutcomeFigures {
  def: OutcomeRowDef;
  students: number | null;
  share: number | null;
  calls: number | null;
  secs: number | null;
  avg: number | null;
}

function figuresOf(row: Record<string, unknown>): OutcomeFigures[] {
  const leads = num(row.allocated);
  return ROWS.map((def) => {
    const students = num(row[def.metric]);
    const calls = def.calls ? num(row[def.calls]) : null;
    const secs = def.secs ? num(row[def.secs]) : null;
    const avg = calls !== null && calls > 0 && secs !== null ? Math.round(secs / calls) : null;
    return { def, students, share: shareOfLeads(students, leads), calls, secs, avg };
  });
}

function Dim({ children }: { children: ReactNode }) {
  return <span className="text-fg-subtle">{children}</span>;
}

function ShareBar({ fig, compact = false }: { fig: OutcomeFigures; compact?: boolean }) {
  if (fig.share === null) return <Dim>{DASH}</Dim>;
  return (
    <div className={cx('flex items-center gap-2', compact && 'justify-end')}>
      <div
        aria-hidden="true"
        className={cx(
          'h-1.5 overflow-hidden rounded-full bg-surface-sunken',
          compact ? 'w-16' : 'min-w-[4rem] flex-1',
        )}
      >
        <div
          className={cx('h-full rounded-full', fig.def.bar)}
          style={{ width: `${Math.min(100, Math.max(fig.share ? 2 : 0, fig.share))}%` }}
        />
      </div>
      <span className="w-14 text-right text-fg-muted tabular-nums">{`${fig.share}%`}</span>
    </div>
  );
}

const figureText = (v: number | null, fmt: (n: number) => string): ReactNode =>
  v === null ? <Dim>{DASH}</Dim> : fmt(v);

function OutcomesSummaryTable({ row }: { row: Record<string, unknown> }) {
  const figures = useMemo(() => figuresOf(row), [row]);
  return (
    <div className="min-w-0">
      <h3 className="mb-2 text-sm font-semibold text-fg">Outcomes</h3>
      <TableWrap className="max-md:hidden">
        <Table className="min-w-[40rem]">
          <THead>
            <TR>
              <TH>Outcome</TH>
              <TH numeric>Students</TH>
              <TH className="w-[28%]">% of leads</TH>
              <TH numeric>Answered calls</TH>
              <TH numeric>Talk time</TH>
              <TH numeric>Avg call time</TH>
            </TR>
          </THead>
          <TBody>
            {figures.map((fig) => (
              <TR key={fig.def.metric}>
                <TD className="whitespace-nowrap font-medium">{fig.def.label}</TD>
                <TD numeric className="font-semibold tabular-nums whitespace-nowrap">
                  {figureText(fig.students, fmtNum)}
                </TD>
                <TD>
                  <ShareBar fig={fig} />
                </TD>
                <TD numeric className="tabular-nums whitespace-nowrap">
                  {figureText(fig.calls, fmtNum)}
                </TD>
                <TD numeric className="tabular-nums whitespace-nowrap">
                  {figureText(fig.secs, fmtDuration)}
                </TD>
                <TD numeric className="tabular-nums whitespace-nowrap">
                  {figureText(fig.avg, fmtShortDuration)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </TableWrap>
      <PiCardList
        items={figures}
        getKey={(fig) => fig.def.metric}
        render={(fig) => (
          <div className="grid min-w-0 gap-1">
            <div className="truncate text-sm font-medium text-fg">{fig.def.label}</div>
            <PiCardField label="Students" value={<span className="font-semibold">{figureText(fig.students, fmtNum)}</span>} />
            <PiCardField label="% of leads" value={<ShareBar fig={fig} compact />} />
            <PiCardField label="Answered calls" value={figureText(fig.calls, fmtNum)} />
            <PiCardField label="Talk time" value={figureText(fig.secs, fmtDuration)} />
            <PiCardField label="Avg call time" value={figureText(fig.avg, fmtShortDuration)} />
          </div>
        )}
      />
    </div>
  );
}

export interface OutcomesBlockProps {
  /** This school's groups from the page's already-loaded list (school × funnel). */
  groups: LaeProdGroupRow[] | null | undefined;
  /** The funnel the modal shows (a slug, or `'all'` / `''` for every funnel). */
  funnel: string;
  /** The modal's hero row (person-deduplicated), used when the school has several groups. */
  totals?: Partial<LaeProdMetrics> | null;
}

/**
 * The outcomes summary (consolidated) and, when the school has groups, the outcome columns of the
 * Schools table per assignment. Nothing here is clickable.
 */
export function OutcomesBlock({ groups, funnel, totals }: OutcomesBlockProps) {
  const list = useMemo<LaeProdGroupRow[]>(() => (Array.isArray(groups) ? groups : []), [groups]);
  const row = useMemo(() => asRecord(consolidateOutcomes(list, totals ?? null)), [list, totals]);
  const lineCount = useMemo(
    () => list.reduce((n, g) => n + (Array.isArray(g.assignments) ? g.assignments.length : 0), 0),
    [list],
  );

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <OutcomesSummaryTable row={row} />
      {list.length > 0 ? (
        <SchoolsTable
          title="Outcomes by assignment"
          groups={list}
          schools={[]}
          funnel={funnel}
          split={lineCount > 1}
          columnGroups={SCHOOL_OUTCOME_GROUPS}
          scope="product-insights.lae.school-modal.outcomes"
          memoryId="lae-modal-outcomes"
          defaultSort={{ key: 'counselling_completed', dir: 'desc' }}
          footer={false}
        />
      ) : null}
    </div>
  );
}
